import React, { createContext, useState, useEffect, ReactNode } from 'react'
import { Theme } from '../config/themes';

// WASM 모듈 import
import { initializeWasm } from '../wasm/wasmutils';
import { fetch_wallet_event_data, fetch_wallet_donation_data, get_server_common_info } from '../wasm/rust_wassm_fetch';
import axios from 'axios';

export interface CommonInfo {
  term_url: string;
  privacy_url: string;
  prepare_url: string;
  telegram_miniapp_url: string;
  forceupdate_txhistory: string;
}

export interface EventInfo {
  idx: number;
  title_en: string;
  title_ko: string;
  contents_en: string;
  contents_ko: string;
  banner_image_ko_url: string;
  banner_image_en_url: string;
  ext_url: string;
  app_screen: string;
  network: string;
  donation_idx: string;
  is_active: string;
}

// 후원하기 페이지 UI 구성
export interface DonationInfo {
  idx: number;
  code: string;
  title_en: string;
  title_ko: string;
  image_en_url: string;
  image_ko_url: string;
  ext_url: string;
  text_en: string;
  text_ko: string;
  check_en: string;
  check_ko: string;
  complete_image_url: string;
  complete_title_en: string;
  complete_title_ko: string;
  complete_contents_en: string;
  complete_contents_ko: string;
  is_active: string;
}

interface SetupContextProps {
	eventInfo: EventInfo[]
  donationInfo: DonationInfo[]
  commonInfo: CommonInfo
  searchEvent: (idx: number) => EventInfo | null
  searchDonation: (code: string) => DonationInfo | null
  forceEventUpdate: () => Promise<void>
  getCommonInfo: () => Promise<CommonInfo>
  completeForceUpdateTxHistory: (network: string) => Promise<boolean>
  checkForceUpdateTxHistory: (network: string) => Promise<boolean>
}

const defaultContext: SetupContextProps = {
	eventInfo: [],
	donationInfo: [],
  commonInfo: {} as CommonInfo,
  searchEvent: () => null,
  searchDonation: () => null,
  forceEventUpdate: () => Promise.resolve(),
  getCommonInfo: () => Promise.resolve({} as CommonInfo),
  completeForceUpdateTxHistory: () => Promise.resolve(false),
  checkForceUpdateTxHistory: () => Promise.resolve(false),
}

export const SetupContext = createContext<SetupContextProps>(defaultContext)

export const SetupProvider = ({ children }: { children: ReactNode }) => {
  const [commonInfo, setCommonInfo] = useState<CommonInfo>();
  const [eventInfo, setEventInfo] = useState<EventInfo[]>([]);
  const [donationInfo, setDonationInfo] = useState<DonationInfo[]>([]);

	useEffect(() => {
		const loadSetup = async () => {
      try{
        // wasm init
        console.warn(`SetupContext 웹 어셈블리 init`)
        await initializeWasm();

        // await forceEventUpdate();
        
      } catch(error) {
        console.error(`SetupContext loadSetup error : `, error)
      }
		}
		loadSetup()
	}, [])

	const fetchData = async (url: string, storageKey: string): Promise<any> => {
    try {
        const response = await fetch(url);

        let data
        if (response.ok) {
            let temp = await response.json();
            if(true === temp.result) {
                // 데이터 검색 성공
                // data = JSON.stringify(temp.data)
                data = JSON.stringify(temp)
            } else {
                console.log(`SetupContextFetching ${storageKey} is null`);
                data = null;
            }
            return data;
        } else {
            throw new Error('Network response was not ok');
        }
    } catch (error) {
        console.log(`SetupContextFetching ${storageKey} failed:`, error);
    }
    return null;
  };

  /**
   * 공통 설정 정보 요청
   * @param storageKey 
   * @param forceUpdate 
   * @returns 
   */ 
  const fetchCommonInfoFromServer = 
	async (
		url: string
	): Promise<any> => {
		try {
				// 로컬 스토리지에 저장된 기본 데이터 있는지 확인
				const res = await get_server_common_info(`${url}`);
        const result = JSON.parse(res)
        console.log(`SetupContext fetchCommonInfoFromServer get_server_common_info : `, result)
        
        let savedData;
				if (result) {
						const response = result;
						console.log(`SetupContext fetchCommonInfoFromServer response`, response.data)

						if(true === response.result) {
								const serverData = response.data;
                console.log(`SetupContext fetchCommonInfoFromServer response JSON.parse : `, serverData)

								const commonData: CommonInfo = {
                    term_url: serverData.ci_term_url,
										privacy_url: serverData.ci_privacy_url,
                    prepare_url: serverData.ci_prepare_url,
                    telegram_miniapp_url: serverData.ci_telegram_miniapp_url,
                    forceupdate_txhistory: serverData.ci_forceupdate_txhistory,
                }
                savedData = JSON.stringify(commonData)
						}
						return savedData;
				} else {
						return null;
				}
		} catch (error) {
				console.error(`SetupContext fetchCommonInfoFromServer failed:`, error);
		}
    return null;
	};

  /**
   * 이벤트배너 설정 정보 요청
   * @param url 
   * @returns 
   */
  const fetchWalletEventData = async (url: string): Promise<any> => {
    try {
        const result = await fetch_wallet_event_data(`${url}`);

        let data
        if (result) {
          const response = await JSON.parse(result);
            if(true === response.result) {
                // 데이터 검색 성공
                // prefix 제거
                const transformedData = response.data.map((item: any) => {
                  const transformedItem: any = {};
                  
                  // Remove wd_ prefix from keys
                  Object.keys(item).forEach(key => {
                    if (key.startsWith('we_')) {
                      transformedItem[key.substring(3)] = item[key];
                    } else {
                      transformedItem[key] = item[key]; 
                    }
                  });

                  return transformedItem;
                });

                data = JSON.stringify(transformedData)
            } else {
                console.log(`SetupContextFetching wallet event data is null`);
                data = null;
            }

            console.log(`SetupContext fetchWalletEventData JSON.stringify data : `, data)
            return data;
        } else {
            throw new Error('SetupContext Network response was not ok');
        }
    } catch (error) {
        console.log(`SetupContextFetching wallet event data failed:`, error);
        alert('An issue occurred while fetching the event data');
    }
    return null;
  };

  const fetchWalletDonationData = async (url: string): Promise<any> => {
    try {
        const result = await fetch_wallet_donation_data(url);

        let data
        if (result) {
          const response = await JSON.parse(result);

          if(true === response.result) {
              // 데이터 검색 성공
              // prefix 제거
              const transformedData = response.data.map((item: any) => {
                const transformedItem: any = {};
                
                // Remove wd_ prefix from keys
                Object.keys(item).forEach(key => {
                  if (key.startsWith('wd_')) {
                    transformedItem[key.substring(3)] = item[key];
                  } else {
                    transformedItem[key] = item[key]; 
                  }
                });

                return transformedItem;
              });

              data = JSON.stringify(transformedData)
          } else {
              console.log(`SetupContextFetching wallet donation data is null`);
              data = null;
          }
          return data;
        } else {
            throw new Error('Network response was not ok');
        }
    } catch (error) {
        console.log(`Fetching wallet donation data failed:`, error);
        alert('An issue occurred while fetching the event data');
    }
    return null;
  };

  // Chrome Storage helper functions
  const setChromeStorage = (data: { [key: string]: any }): Promise<void> => {
    return new Promise((resolve, reject) => {
        chrome.storage.local.set(data, () => {
            if (chrome.runtime.lastError) {
                reject(new Error(chrome.runtime.lastError.message)); // lastError를 Error 객체로 변환
            } else {
                resolve(); // 데이터가 성공적으로 저장된 경우
            }
        });
    });
  };

  const getChromeStorage = async (keys: string[]) => {
    return new Promise<any>((resolve, reject) => {
        chrome.storage.local.get(keys, (result) => {
            if (chrome.runtime.lastError) {
                return reject(chrome.runtime.lastError);
            }
            resolve(result);
        });
    });
  };
  
  const saveData = async (key:string, data:any): Promise<any> => {
    await localStorage.setItem(key, data);
  }

  const loadData = async (key:string): Promise<any> => {
    const loadData = await localStorage.getItem(key)
    return loadData;
  }

  /**
   * 공통 설정 정보 요청
   * @param forceUpdate 
   * @returns 
   */
  const getCommonInfo = async (forceUpdate:boolean=false): Promise<any> => {
    const commonInfoKey = 'commonInfo';

    // 로컬에 저장된 공통설정 데이터 있는지 확인
    let returnData = await loadData(commonInfoKey)
    
    if(!returnData || true === forceUpdate) {
      // 서버에 공통 설정 데이터 요청
      if(null != process.env.REACT_APP_QWALLET_SERVER_URL && '' !== process.env.REACT_APP_QWALLET_SERVER_URL) {
        const temp = process.env.REACT_APP_QWALLET_SERVER_URL.toString()
        returnData = await fetchCommonInfoFromServer(temp);
      } else {
          // 설정된 URL 없을 경우 이벤트리스트 데이터 초기화
          returnData = ''
      }

      // 로컬 스토리지에 이벤트 데이터 저장
      await saveData(commonInfoKey, returnData || '')
    }

    return returnData;
  };

  /**
   * 이벤트배너 설정 정보 요청
   * @param forceUpdate 
   * @returns 
   */
  const getDefaultEvents = async (forceUpdate:boolean=false): Promise<any> => {
    const eventDataKey = 'eventData';

    // 로컬에 저장된 이벤트 데이터 있는지 확인
    let returnData = await loadData(eventDataKey)
    
    if(!returnData || true === forceUpdate) {
      // 서버에 이벤트 설정 데이터 요청
      if(null != process.env.REACT_APP_QWALLET_SERVER_URL && '' !== process.env.REACT_APP_QWALLET_SERVER_URL) {
        const temp = process.env.REACT_APP_QWALLET_SERVER_URL.toString()
        returnData = await fetchWalletEventData(temp);
      } else {
          // 설정된 URL 없을 경우 이벤트리스트 데이터 초기화
          returnData = ''
      }

      // 로컬 스토리지에 이벤트 데이터 저장
      await saveData(eventDataKey, returnData || '')
    }

    return returnData;
  };

  const getDefaultDonation = async (forceUpdate:boolean=false): Promise<any> => {
    const donationDataKey = 'donationData';

    // 로컬에 저장된 후원페이지 데이터 있는지 확인
    let returnData = await loadData(donationDataKey)
    console.log(`SetupContext  getDefaultDonation returnData : `, returnData)

    if(!returnData || true === forceUpdate) {
      // 서버에 후원페이지 설정 데이터 요청
      if(null != process.env.REACT_APP_QWALLET_SERVER_URL && '' !== process.env.REACT_APP_QWALLET_SERVER_URL) {
        returnData = await fetchWalletDonationData(process.env.REACT_APP_QWALLET_SERVER_URL);

      } else {
          // 설정된 URL 없을 경우 후원리스트 데이터 초기화
          returnData = ''
      }

      // 로컬 스토리지에 후원페이지 구성 데이터 저장
      await saveData(donationDataKey, returnData || '')
    }

    return returnData;
  };

  const searchEvent = (idx: number): EventInfo | null => {
    if (!eventInfo) return null;
    return eventInfo.find(event => event.idx === idx) || null;
  };

  const searchDonation = (code: string): DonationInfo | null => {
    if (!donationInfo) return null;
    return donationInfo.find(event => event.code.toLowerCase() === code.toLowerCase()) || null;
  };

  /**
	 * @brief 전송내역 강제갱신 완료 상태 설정
	 * @param network string 
	 * @returns 
	 */
	const completeForceUpdateTxHistory = async (network: string) => {
		try {
			const adminForceUpdate = commonInfo?.forceupdate_txhistory;
      console.error('SetupContext completeForceUpdateTxHistory', adminForceUpdate);

			if (adminForceUpdate != null) {
				const value = adminForceUpdate;
        const key = `forceUpdateTxHistory_${network}`;
				localStorage.setItem(key, value);
				return true;
			}
		} catch (error) {
			console.error('Error Set forceUpdate TxHistory', error);
		}
		return false;
	};

  /**
	 * @brief 전송내역 강제갱신 상태값 확인
   * @param network string 
	 * @return boolean true:강제갱신, false:갱신없음
	 */
	const checkForceUpdateTxHistory = async (network: string) => {
		try {
      // 관리자가 설정한 tx 강제 업데이트 상태값 확인
      const adminForceUpdate = commonInfo?.forceupdate_txhistory;
      
      // 관리자 설정한 tx 강제 업데이트 상태값이 있을 경우
			if(null != adminForceUpdate) {
        // 로컬 저장된 강제 tx 업데이트 상태값 확인
        const key = `forceUpdateTxHistory_${network}`;
        const lastForceUpdate = localStorage.getItem(key)

        // commonInfo 설정값과 로컬 저장된 상태값 비교
        if(adminForceUpdate.toString() !== lastForceUpdate?.toString()) {
          return true;
        }
			}
		} catch (error) {
			console.error('Error Get forceUpdate TxHistory', error);
		}

    console.log(`SetupContext checkForceUpdateTxHistory TX 강제 업데이트 안함 : `)
		return false;
	};

  // 강제 공통설정, 이벤트배너 설정 업데이트
  const forceEventUpdate = async (): Promise<void> => {
    const fetchCommonInfo = await getCommonInfo(true)
    if (fetchCommonInfo) {
      const parsedCommonInfoData = JSON.parse(fetchCommonInfo);
      console.log(`SetupContext forceEventUpdate parsedCommonInfoData : `, parsedCommonInfoData)
      // const commonInfoArray = Object.keys(parsedCommonInfoData).map(key => parsedCommonInfoData[key]);
      setCommonInfo(parsedCommonInfoData);
    }

    const fetchEvents = await getDefaultEvents(true)
    if (fetchEvents) {
        const parsedEventData = JSON.parse(fetchEvents);
        const eventsArray = Object.keys(parsedEventData).map(key => parsedEventData[key]);
        setEventInfo(eventsArray);
    }

    // const fetchDonation = await getDefaultDonation(true)
    // if (fetchDonation) {
    //     const parsedDonationData = JSON.parse(fetchDonation);
    //     const donationArray = Object.keys(parsedDonationData).map(key => parsedDonationData[key]);
    //     setDonationInfo(donationArray);
    // }
  };

	return <SetupContext.Provider value={{ 
    eventInfo, 
    donationInfo,
    commonInfo: commonInfo || defaultContext.commonInfo,
    searchEvent, 
    searchDonation, 
    forceEventUpdate,
    getCommonInfo,
    completeForceUpdateTxHistory,
    checkForceUpdateTxHistory
    }}>
      {children}
    </SetupContext.Provider>
}
